import React, { useState, useEffect, useRef } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Box,
  Button,
  useDisclosure,
  Checkbox,
  CheckboxGroup,
  ModalBody,
  ModalFooter,
  Flex,
  useToast,
  Text,
  Divider,
} from "@chakra-ui/react";
import { Accordian } from "../../components/Accordian";
import { ModalBox } from "../../components/ModalBox";
import { MY_DYNOES, CLASS_MODULES } from "../../graphql/queries";
import {
  CREATE_MODULE,
  UPDATE_MODULE,
  REMOVE_DYNO_FROM_MODULE,
} from "../../graphql/mutations";
import { AddIcon } from "@chakra-ui/icons";
import { EditableInputComponent } from "../../components/EditableInput";
import { DynoName } from "../../components/DynoName";
import { addToClassStatusId } from "../../config";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import { fetchGql } from "../../utils/xhr";
import { AlertDialog } from "../../components/AlertDialog";

export const ContentPage = ({ user, userRole, classId }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState({ id: "" });
  const [checkedItems, setCheckedItems] = useState([]);
  const history = useHistory();
  const {
    isOpen: alertIsOpen,
    onOpen: alertOnOpen,
    onClose: alertOnClose,
  } = useDisclosure();
  const removeDynoRef = useRef({});

  const [getMyDynoes, { loading, data }] = useLazyQuery(MY_DYNOES, {
    fetchPolicy: "cache-and-network",
  });
  const [createClassModule] = useMutation(CREATE_MODULE, {
    update: async (cache, { data }) => {
      const cached_modules = cache.readQuery({
        query: CLASS_MODULES,
        variables: {
          where: {
            classId: classId,
            isStudent: userRole === "Student",
            ...(userRole === "Student" ? { userId: user.id } : {}),
          },
        },
      });
      let modules = [
        ...cached_modules.getPracticeModules,
        data.createCollectionModule,
      ];
      cache.writeQuery({
        query: CLASS_MODULES,
        variables: {
          where: {
            classId: classId,
            isStudent: userRole === "Student",
            ...(userRole === "Student" ? { userId: user.id } : {}),
          },
        },
        data: {
          getPracticeModules: modules,
        },
      });
      toast({
        position: "top",
        title: "Added module",
        status: "success",
        duration: 9000,
        isClosable: true,
      });
    },
    onError: () => {
      toast({
        position: "top",
        title: "Failed to add module. Please try again",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    },
  });
  const [updateClassModule, { loading: updateModuleLoading }] = useMutation(
    UPDATE_MODULE,
    {
      update: async (cache, { data }) => {
        const cached_modules = cache.readQuery({
          query: CLASS_MODULES,
          variables: {
            where: {
              classId: classId,
              isStudent: userRole === "Student",
              ...(userRole === "Student" ? { userId: user.id } : {}),
            },
          },
        });
        let modules = [...cached_modules.getPracticeModules];
        const index = modules.findIndex(
          (m) => m.id === data.updateCollectionModule.id
        );
        const dynoes = data.updateCollectionModule.dynosUnderModule
          .filter(
            (d) =>
              !modules[index].dynosUnderModule.map((d) => d.id).includes(d.id)
          )
          .map((d) => ({ ...d, dynosStats: 0 }));
        modules[index] = {
          ...modules[index],
          dynosUnderModule: [...modules[index].dynosUnderModule, ...dynoes],
        };
        cache.writeQuery({
          query: CLASS_MODULES,
          variables: {
            where: {
              classId: classId,
              isStudent: userRole === "Student",
              ...(userRole === "Student" ? { userId: user.id } : {}),
            },
          },
          data: {
            getPracticeModules: modules,
          },
        });
        onClose();
        const mutation = (dynoId, statusId) => {
          return `
            mutation {
              updateDyno(
                where: { id: "${dynoId}" }, 
                data: {
                status: {
                  connect: {
                    id: "${statusId}",
                  },
                },
              }) {
                id
              }
            }
          `;
        };
        const request = await Promise.allSettled(
          dynoes.map((d) => fetchGql(mutation(d.id, addToClassStatusId)))
        );
        toast({
          position: "top",
          title: "Added Dyno",
          status: "success",
          duration: 9000,
          isClosable: true,
        });
      },
      onError: () => {
        onClose();
        toast({
          position: "top",
          title: "Failed to add dyno. Please try again",
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      },
    }
  );
  const [updateModule] = useMutation(UPDATE_MODULE, {
    update: async (cache, { data }) => {
      const cached_modules = cache.readQuery({
        query: CLASS_MODULES,
        variables: {
          where: {
            classId: classId,
            isStudent: userRole === "Student",
            ...(userRole === "Student" ? { userId: user.id } : {}),
          },
        },
      });
      let modules = [...cached_modules.getPracticeModules];
      const index = modules.findIndex(
        (m) => m.id === data.updateCollectionModule.id
      );
      modules[index] = {
        ...modules[index],
        moduleName: data.updateCollectionModule.moduleName,
      };
      cache.writeQuery({
        query: CLASS_MODULES,
        variables: {
          where: {
            classId: classId,
            isStudent: userRole === "Student",
            ...(userRole === "Student" ? { userId: user.id } : {}),
          },
        },
        data: {
          getPracticeModules: modules,
        },
      });
      toast({
        position: "top",
        title: "Updated module name",
        status: "success",
        duration: 9000,
        isClosable: true,
      });
    },
    onError: () => {
      toast({
        position: "top",
        title: "Failed to update module name",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    },
  });
  const [removeDynoFromMod, { loading: removeDynoLoading }] = useMutation(
    REMOVE_DYNO_FROM_MODULE,
    {
      update: async (cache, { data }) => {
        const cached_modules = cache.readQuery({
          query: CLASS_MODULES,
          variables: {
            where: {
              classId: classId,
              isStudent: userRole === "Student",
              ...(userRole === "Student" ? { userId: user.id } : {}),
            },
          },
        });
        let modules = [...cached_modules.getPracticeModules];
        const index = modules.findIndex(
          (m) => m.id === data.removeDynoFromModule.moduleId
        );
        const dynoes = modules[index].dynosUnderModule.filter(
          (d) => d.id !== data.removeDynoFromModule.dynoId
        );
        modules[index] = {
          ...modules[index],
          dynosUnderModule: dynoes,
        };
        cache.writeQuery({
          query: CLASS_MODULES,
          variables: {
            where: {
              classId: classId,
              isStudent: userRole === "Student",
              ...(userRole === "Student" ? { userId: user.id } : {}),
            },
          },
          data: {
            getPracticeModules: modules,
          },
        });
        alertOnClose();
        toast({
          position: "top",
          title: "Dyno removed",
          status: "success",
          duration: 9000,
          isClosable: true,
        });
      },
      onError: () => {
        toast({
          position: "top",
          title: "Failed to remove dyno",
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      },
    }
  );
  const [getClassModules] = useLazyQuery(CLASS_MODULES, {
    onCompleted: (data) => {
      if (userRole === "Student") {
        const formatStr = "MMM D, YYYY h:mm A";
        let pracModules = [...data.getPracticeModules];
        pracModules = pracModules.map((mod) => ({
          ...mod,
          dynosUnderModule: mod.dynosUnderModule.map((d) => ({
            ...d,
            dynosStats:
              d.dynosStats !== 0
                ? format(d.dynosStats, formatStr)
                : d.dynosStats,
          })),
        }));
        setModules(pracModules);
      } else {
        setModules(data.getPracticeModules);
      }
    },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy:
      userRole === "Student" ? "cache-and-network" : "cache-only",
  });

  const openMyDynoModal = (moduleId) => {
    setSelectedModule({ id: moduleId });
    getMyDynoes({
      variables: {
        where: {
          owner: {
            id: {
              equals: user.id,
            },
          },
          status: { statusType: { equals: "Published" } },
        },
      },
    });
    onOpen();
  };

  const createModule = () => {
    createClassModule({
      variables: {
        data: {
          moduleName: "Module Name",
          organizationClass: {
            connect: {
              id: classId,
            },
          },
        },
      },
    });
  };

  const onCheckItem = (e) => {
    setCheckedItems([...checkedItems, e.target.value]);
  };

  const addToModule = () => {
    updateClassModule({
      variables: {
        where: {
          id: selectedModule.id,
        },
        data: {
          dynosUnderModule: {
            connect: checkedItems.map((item) => ({ id: item })),
          },
        },
      },
    });
  };

  const updateModuleName = (name, moduleId, moduleName) => {
    if (name !== moduleName) {
      updateModule({
        variables: {
          where: {
            id: moduleId,
          },
          data: {
            moduleName: {
              set: name,
            },
          },
        },
      });
    }
  };
  const removeDyno = () => {
    removeDynoFromMod({
      variables: {
        where: {
          dynoId: removeDynoRef.current.dynoId,
          moduleId: removeDynoRef.current.moduleId,
        },
      },
    });
  };
  const viewSubmissions = (dynoId) => {
    history.push(`/submission/${dynoId}`);
  };

  useEffect(() => {
    getClassModules({
      variables: {
        where: {
          classId: classId,
          isStudent: userRole === "Student",
          ...(userRole === "Student" ? { userId: user.id } : {}),
        },
      },
    });
  }, [classId, getClassModules, user.id, userRole]);

  return (
    <Box
      style={{
        maxWidth: "1180px",
        margin: "0 auto",
        padding: "30px 20px",
      }}
    >
      {userRole !== "Student" && (
        <Flex justifyContent={"flex-end"}>
          <Button
            leftIcon={<AddIcon />}
            colorScheme="blue"
            variant="solid"
            onClick={createModule}
          >
            Add module
          </Button>
        </Flex>
      )}
      {modules.length > 0 &&
        React.Children.toArray(
          modules.map((module) => (
            <Accordian
              children1={
                <Flex
                  width="100%"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  {userRole !== "Student" ? (
                    <>
                      <EditableInputComponent
                        title={module.moduleName}
                        update={(name) =>
                          updateModuleName(name, module.id, module.moduleName)
                        }
                      />
                    </>
                  ) : (
                    <Text>{module.moduleName}</Text>
                  )}
                </Flex>
              }
              children2={
                <Button
                  leftIcon={<AddIcon />}
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    openMyDynoModal(module.id);
                  }}
                  colorScheme="blue"
                  textTransform="capitalize"
                >
                  add dyno
                </Button>
              }
              children3={
                module.dynosUnderModule &&
                React.Children.toArray(
                  module.dynosUnderModule.map((dyno) => (
                    <>
                      <DynoName
                        title={dyno.dynoTitle}
                        userRole={userRole}
                        dyno={dyno}
                        classId={classId}
                        removeDyno={() => {
                          removeDynoRef.current = {
                            dynoId: dyno.id,
                            moduleId: module.id,
                          };
                          alertOnOpen();
                        }}
                        viewSubmissions={() => viewSubmissions(dyno.id)}
                      />
                      <Divider />
                    </>
                  ))
                )
              }
            />
          ))
        )}
      <ModalBox
        isOpen={isOpen}
        onClose={onClose}
        title="Add Dynos"
        loading={loading}
      >
        <ModalBody padding="0">
          <CheckboxGroup colorScheme="blue">
            <Flex flexDirection="column" direction={["column", "row"]}>
              {data &&
                React.Children.toArray(
                  data.dynoes.map((dyno) => (
                    <>
                      <Checkbox
                        value={dyno.id}
                        onChange={onCheckItem}
                        padding="15px"
                      >
                        {dyno.dynoTitle}
                      </Checkbox>
                      <Divider />
                    </>
                  ))
                )}
              {!loading && data?.dynoes?.length === 0 && (
                <Text style={{ padding: "10px 20px" }}>
                  It seem's that you haven't created any dyno yet or the dyno is
                  not ready to share.
                </Text>
              )}
            </Flex>
          </CheckboxGroup>
        </ModalBody>
        {!loading && (
          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={() => addToModule(user)}
              isLoading={updateModuleLoading}
              disabled={checkedItems.length === 0}
            >
              Add
            </Button>
            {!updateModuleLoading && <Button onClick={onClose}>Cancel</Button>}
          </ModalFooter>
        )}
      </ModalBox>
      <AlertDialog
        title="Remove dyno from class"
        description="Are you sure? You can't undo this action afterwards. Removing dyno will delete all the submissions and reports from the class."
        actionButtonText="Remove"
        isOpen={alertIsOpen}
        onCancel={alertOnClose}
        onDelete={removeDyno}
        isWorking={removeDynoLoading}
        loadingText="Removing"
      />
    </Box>
  );
};
